/**
 * The shared `chat-ws` socket.
 *
 * One module singleton, refcounted rather than owned by whichever component
 * mounted it — a navigation between two threads can have the outgoing one's
 * cleanup run after the incoming one's effect, and closing the socket there
 * would drop the new thread's subscribe. Only the last `releaseChatSocket`
 * disconnects it.
 *
 * THAT DISCONNECT IS ITSELF DEFERRED, one tick, for the refcount-hits-zero
 * case the paragraph above doesn't cover: a single consumer whose OWN effect
 * tears down and rebuilds — e.g. a dependency change re-running cleanup then
 * setup in the same synchronous flush — releases and immediately
 * re-acquires with no other holder in between. Disconnecting synchronously
 * on that release would destroy and rebuild the connection for nothing
 * genuinely given up: a fresh handshake, a fresh `getSession()`, and a
 * resubscribe burst for every room the reacquiring consumer wants. See
 * `useInboxSocket`'s membership effect for a case this used to bite.
 *
 * `auth` is passed as a CALLBACK, not a static object, and that is the whole
 * reason this module exists rather than a bare `io(url, { auth: { token } })`
 * at each call site. socket.io freezes an object auth payload at
 * construction and replays that same value on every reconnect; a Supabase
 * access token expires in an hour, so a socket that outlives one would
 * reconnect with a dead token, get refused, and sit there believing it is
 * subscribed while receiving nothing. The callback re-runs on every connect
 * and reconnect, so each one re-reads the session — and `getSession()`
 * refreshes an expired one.
 *
 * `transports: ["websocket"]` matches the gateway, which serves no polling
 * fallback. Letting the default stand would make a client that cannot
 * upgrade silently fall back to long-polling and keep "working" — which is
 * worse than failing, since a broken upgrade path in front of a load
 * balancer would go unnoticed.
 */
import * as Sentry from "@sentry/nextjs";
import { io, type Socket } from "socket.io-client";
import { env } from "~/env";
import { createClient } from "~/lib/supabase/client";

let socket: Socket | null = null;
let refCount = 0;
// Set while a refcount-hits-zero disconnect is scheduled but hasn't fired
// yet — cleared (and the disconnect cancelled) if `acquireChatSocket` claims
// the still-live socket back before then.
let pendingDisconnect: ReturnType<typeof setTimeout> | null = null;

// Cancels the current socket's pending reconnect timer, if any — reassigned
// on every `createSocket` call. `Socket.disconnect()` only emits its own
// `disconnect` event (and so only self-cleans) when the socket had actually
// reached `connected`; one stuck retrying a terminal handshake refusal never
// did, so `releaseChatSocket` calls this explicitly to avoid a stray
// `created.connect()` firing after `socket` has already been nulled out.
let cancelPendingReconnect: (() => void) | null = null;

// One report per page load — cleared never, so a socket recreated later this
// page load (see `releaseChatSocket`) doesn't restate the same finding once
// its own retries exhaust.
let reportedTerminalFailure = false;

/**
 * The three handshake refusals `ChatGateway`'s auth middleware can return.
 * Unlike a transient transport error, socket.io's manager treats these as
 * fatal: it calls `destroy()` on the socket and marks it `active: false`,
 * which means the manager itself will never redial — see `connect_error`
 * below, which is what actually retries these.
 */
const TERMINAL_HANDSHAKE_ERRORS = new Set([
  "unauthorized",
  "origin_not_allowed",
  "service_unavailable",
]);

/**
 * Doubling backoff for redialing after a terminal handshake refusal. The
 * OAuth race this repo's CLAUDE.md documents (session not yet hydrated right
 * after page load) reads on the wire as the exact same `unauthorized`
 * refusal as a genuinely dead session — the only way to tell them apart is
 * to actually retry and let `getAccessToken()` re-read `getSession()` on
 * each attempt.
 *
 * `as const` plus the `?? FALLBACK_DELAY_MS` at each read site (rather than
 * indexing this bare) is deliberate: under this repo's
 * `noUncheckedIndexedAccess`, `RECONNECT_BACKOFF_MS[i]` still types as
 * `number | undefined` for a non-literal `i` either way, and `setTimeout`
 * silently accepts `undefined` as "fire immediately" — so an off-by-one here
 * would type-check into a 0ms retry loop instead of a compile error. The
 * fallback keeps a badly-indexed read capped at the slowest interval rather
 * than silent.
 */
const RECONNECT_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000] as const;
const FALLBACK_DELAY_MS = 16_000;

/**
 * `service_unavailable` — the gateway itself being down — is the one
 * terminal reason that doesn't get to run out of retries: a deploy or
 * restart routinely outlasts the ~31s bounded schedule above, and once that
 * schedule is exhausted nothing else redials this socket (see
 * `acquireChatSocket` — it hands the same, now-permanently-dead instance to
 * every later consumer for the rest of the page's life). `unauthorized` and
 * `origin_not_allowed` keep the bounded schedule: the former is usually
 * settled well within it, and retrying the latter — a config error —
 * forever would be harmless but pointless.
 */
const UNBOUNDED_RETRY_ERRORS = new Set(["service_unavailable"]);

async function getAccessToken(): Promise<string | null> {
  const { data } = await createClient().auth.getSession();
  return data.session?.access_token ?? null;
}

function createSocket(url: string): Socket {
  const created = io(url, {
    transports: ["websocket"],
    auth: (callback) => {
      // The callback MUST fire on both paths. A rejected `getSession()` that
      // never calls back leaves the handshake open forever — no
      // `connect_error`, no retry, no Sentry signal, and no realtime for the
      // rest of the page's life. Handing over a null token instead lets the
      // gateway refuse it and produces a real, observable failure.
      getAccessToken()
        .then((token) => callback({ token }))
        .catch(() => callback({ token: null }));
    },
  });

  // Scoped to this socket instance, not the module — a freshly created
  // socket (e.g. after `releaseChatSocket` disposes the old one) starts its
  // own backoff rather than inheriting a spent one.
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };
  cancelPendingReconnect = clearReconnectTimer;

  created.on("connect", () => {
    reconnectAttempt = 0;
    clearReconnectTimer();
    Sentry.addBreadcrumb({
      category: "chat-ws",
      message: "connected",
      level: "info",
    });
  });

  created.on("disconnect", (reason) => {
    Sentry.addBreadcrumb({
      category: "chat-ws",
      message: `disconnected: ${reason}`,
      level: "info",
    });
  });

  // Lifecycle only — no message bodies, conversation ids, or user ids. This
  // is a feature whose entire payload is private correspondence.
  created.on("connect_error", (error) => {
    const reason = error.message;
    Sentry.addBreadcrumb({
      category: "chat-ws",
      message: `connect_error: ${reason}`,
      level: "warning",
    });

    if (!TERMINAL_HANDSHAKE_ERRORS.has(reason) || created.active) {
      // Either a transient transport error, or a refusal socket.io's own
      // manager is already going to redial on its own — nothing to do here.
      return;
    }

    const scheduleExhausted = reconnectAttempt >= RECONNECT_BACKOFF_MS.length;

    // Report once, the moment the bounded schedule first runs out — even for
    // `service_unavailable`, which keeps retrying past this point below. The
    // report is what pages someone that the gateway looks down; it must not
    // wait on retries that are, by design, never going to stop on their own.
    if (scheduleExhausted && !reportedTerminalFailure) {
      reportedTerminalFailure = true;
      Sentry.captureMessage(`Chat socket handshake refused: ${reason}`, {
        level: "warning",
      });
    }

    if (scheduleExhausted && !UNBOUNDED_RETRY_ERRORS.has(reason)) {
      return;
    }

    const delay = RECONNECT_BACKOFF_MS[reconnectAttempt] ?? FALLBACK_DELAY_MS;
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      created.connect();
    }, delay);
  });

  return created;
}

/**
 * Acquire the shared socket, connecting it on first use. Returns `null` —
 * with no connection attempted at all — when `NEXT_PUBLIC_CHAT_WS_URL` is
 * unset, so chat stays fully REST-only in any environment that hasn't wired
 * up `chat-ws` yet.
 */
export function acquireChatSocket(): Socket | null {
  const url = env.NEXT_PUBLIC_CHAT_WS_URL;
  if (!url) return null;

  if (pendingDisconnect) {
    clearTimeout(pendingDisconnect);
    pendingDisconnect = null;
  }

  if (!socket) socket = createSocket(url);
  refCount += 1;
  return socket;
}

/** Pair every `acquireChatSocket` call with exactly one of these. */
export function releaseChatSocket(): void {
  if (refCount === 0) return;

  refCount -= 1;
  if (refCount === 0 && socket) {
    // Not disconnected here directly — see the module comment. `socket`
    // itself is left set so a same-tick re-acquire above reuses it instead
    // of building a new one; only the deferred callback below ever nulls it.
    const stale = socket;
    pendingDisconnect = setTimeout(() => {
      pendingDisconnect = null;
      cancelPendingReconnect?.();
      stale.disconnect();
      socket = null;
    }, 0);
  }
}
